import "./StandUpCardComponent.scss";

import { Button, Flex, Image, Segment, Text } from "@fluentui/react-northstar";
import { NominalQuestion, TeamsApplicationTypes } from "@teams/types";
import { ScheduleDTO, SurveyDTO, UserDTO } from "@teams/types/api";
import moment from "moment";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import AgilePendingImage from "../../../../_shared/assets/agile-pending-image.svg";
import CorePendingImage from "../../../../_shared/assets/core-pending-image.svg";
import {
  HeroUnit,
  agileHeroUnitStyles,
  coreHeroUnitStyles,
} from "../../../../_shared/components/HeroUnit";
import { SeriesMenu } from "../../../../_shared/components/Menus/SeriesMenu/SeriesMenu";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { clientEventTracker } from "../../../../_shared/utils/clientTelemetry";
import { getPageContext } from "../../../../_shared/utils/teamsHelpers";
import { AgileHeaderComponent } from "../../components/AgileHeader/AgileHeaderComponent";

type StandUpCardComponentProps = {
  applicationType: TeamsApplicationTypes;
  standup: SurveyDTO;
  user: UserDTO;
  title: string;
  allowShowResults?: boolean;
  schedule: ScheduleDTO | undefined;
  groupId: string;
  detailsBarText: string;
  seriesDisabled: boolean;
  showOwnerOptions: boolean;
  onChanged: (seriesId: string) => void;
};

type DetailedCompletionsSection = { label: string; names: string[]; numResponses: number };

export const StandUpCard: React.FC<StandUpCardComponentProps> = ({
  applicationType,
  standup,
  user,
  title,
  schedule,
  detailsBarText,
  onChanged,
}) => {
  const { responses, users } = useMemo(() => {
    const maxNameCount = 3;
    const responses = (standup.responses || []).sort((a, b) =>
      a.updatedAt > b.updatedAt ? -1 : b.updatedAt > a.updatedAt ? 1 : 0
    );
    const users = responses.slice(0, maxNameCount).map(response => response.user.name);

    return { responses, users };
  }, [standup]);

  const generateDetailedCompletions = (): React.ReactNode => {
    const nominalQuestion = standup.questions.find(
      question => question.questionType === "nominal"
    ) as NominalQuestion;

    if (!nominalQuestion) {
      return (
        <Text
          className="standup-card__response-detailed-row"
          content={`${standup.responseCount} responses`}
          align="center"
          size="large"
          weight="bold"
        />
      );
    }

    const groupedChoices: { [key: string]: { value: string; users: string[] } } = {};
    nominalQuestion.choices.forEach(
      choice => (groupedChoices[choice.id] = { value: choice.value, users: [] })
    );

    if (standup?.responses) {
      standup.responses
        .sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : b.updatedAt > a.updatedAt ? 1 : 0))
        .forEach(response => {
          const userResponse = response.responses.find(r => r.questionId === nominalQuestion.id);
          if (userResponse) {
            groupedChoices[userResponse.value as string]?.users.push(response.user.name);
          }
        });
    }

    const sections: DetailedCompletionsSection[] = Object.values(groupedChoices).map(
      groupedChoice => {
        const numResponses = groupedChoice.users.length;
        const names = groupedChoice.users.slice(0, 10);
        return { label: groupedChoice.value, names, numResponses };
      }
    );

    return sections.map((choice: DetailedCompletionsSection, index) => {
      const label = (
        <b>
          {choice.label} ({choice.numResponses}):
        </b>
      );
      const displayNames = choice.names.length ? choice.names.join(", ") : <i>None</i>;
      const additionalResponses =
        choice.numResponses > choice.names.length ? (
          <b>+{choice.numResponses - choice.names.length} More</b>
        ) : null;

      return (
        <div key={index} className="standup-card__response-detailed-row">
          {label} {displayNames} {additionalResponses}
        </div>
      );
    });
  };

  const PendingImage =
    standup?.applicationType === "msteams" ? CorePendingImage : AgilePendingImage;
  const appHeroUnitStyles =
    schedule?.applicationType === "msteams" ? coreHeroUnitStyles : agileHeroUnitStyles;

  return (
    <Segment color="brand" className="standup-card">
      <div className="standup-card__container">
        <AgileHeaderComponent title={title} detailsBarText={detailsBarText} />
        <div className="standup-card__details-section">
          <HeroUnit style={appHeroUnitStyles}>
            <Text
              styles={{ marginBottom: "10px" }}
              align="center"
              weight="light"
              color="gray"
              content={`🕘 ${standup.state === "closed" ? "Closed" : "Closes"}: ${moment(
                standup.closeAt
              ).format("MMM Do [at] LT")}`}
            />
            {standup.state === "pending" && <Image src={PendingImage} alt="Stand Up" />}
            {schedule && (
              <Text
                className="standup-card__next"
                align="center"
                weight="light"
                color="gray"
                content={`📅 Next: ${moment(schedule.next).format("MMM Do [at] LT")}`}
              />
            )}
            <div className="standup-card__response-details">{generateDetailedCompletions()}</div>
          </HeroUnit>
          <div className="standup-card__response-summary">
            <Text weight="bold">Completions ({responses.length}): </Text>
            {users.join(", ")}
            {users.length < responses.length && (
              <Text weight="bold" content={` +${responses.length - users.length} More`} />
            )}
          </div>
          <div>
            {schedule && (
              <Flex vAlign="center">
                <Flex.Item grow>
                  <div className="standup-card__autolink">
                    {schedule.autolinkIntegrations && schedule.autolinkIntegrations.length > 0 && (
                      <PollyIcon
                        icon="integration-icon"
                        label={`Integrations (${schedule.autolinkIntegrations.length})`}
                        inheritColor
                      />
                    )}
                  </div>
                </Flex.Item>
                <SeriesMenu
                  user={user}
                  schedule={schedule}
                  onChanged={() => onChanged(schedule.id)}
                />
              </Flex>
            )}
            <Flex gap="gap.smaller" className="standup-card__footer" hAlign="end" space="around">
              {standup.id && (
                <Button
                  to={`${getPageContext()}/content/polly/survey/${standup.id}`}
                  as={Link}
                  className="standup-card__view-responses"
                  primary
                  content="Current responses"
                />
              )}
              {schedule && (
                <Button
                  data-tname="show-historical-responses"
                  className="standup-card__analysis-link"
                  to={`/${applicationType}/content/polly/series/${schedule.id}`}
                  as={Link}
                  onClick={() => {
                    clientEventTracker.track("HistoricalResponses clicked", user, {
                      scheduleId: schedule.id,
                    });
                  }}
                  primary
                  content="Historical responses"
                />
              )}
            </Flex>
          </div>
        </div>
      </div>
    </Segment>
  );
};
